/**
 * The DRUM LIGHTS panel (2026-10-23): what the timeline editor's detail panel shows for a drum lights block, and its
 * "Try it" preview. Model, presets and the preview maths: `timeline/drumLights.ts`; how a hit draws: `hitMix.ts`.
 *
 * Built for the operator's ask (OPApps/opbridge-opbuttons.md, the S336 preset-block ask): "super simple to set up",
 * and ours: "pro grade that a novice could play around with and have fun". So every choice is a tap with a line under
 * it saying what it does, nothing needs typing, and "Try it" shows the block on screen at the timeline's tempo, a hit
 * at a time or as a beat that runs into the fill, drawn with the same rule OPBridge draws with. What it cannot show
 * (the live drums, OPBridge's flash limit) is said under it rather than faked.
 */
import { createMemo, createSignal, For, onCleanup, Show, type JSX } from 'solid-js';
import {
  hitBlockCycles, hitBlockFill,
  type HitBlockLook, type HitBlockStep, type InstrumentColor, type InstrumentLookStyle, type InstrumentLookVary, type Timeline, type TimelineLane,
} from '@opbuttons/schema';
import { fixtures } from '../fixtures/store.ts';
import { STAGE_COLORS } from '../lighting/palette.ts';
import { placeStep, replaceStep, setTimelineStrobes } from '../timeline/store.ts';
import {
  CYCLE_CHOICES, DRUMS, FALL_CHOICES, FILL_CHOICES, STYLE_HINTS, STYLE_NAMES, VARY_HINTS, VARY_NAMES, WHITE,
  blockLookLights, cssColour, grooveFor, lookableLights, previewColours, sourceLabel, type PreviewHit,
} from '../timeline/drumLights.ts';

type PropFn = (p: { label: string; children: JSX.Element }) => JSX.Element;

const VARIES: InstrumentLookVary[] = ['none', 'next', 'random', 'alternate'];
const STYLES: InstrumentLookStyle[] = ['flash', 'pulse', 'strobe'];
/** "Softer hits": how much velocity dims a hit (`velocitySensitivity`; absent = 1). */
const SOFT_CHOICES: ReadonlyArray<{ s: number; name: string; hint: string }> = [
  { s: 1, name: 'Dimmer', hint: 'A softer hit is dimmer, all the way down: how hard you play shows.' },
  { s: 0.5, name: 'A bit', hint: 'A softer hit dims a little, never below half.' },
  { s: 0, name: 'Full', hint: 'Every hit at full brightness, however hard it is played.' },
];

const near = (a: InstrumentColor, b: { r: number; g: number; b: number }) => Math.abs(a.r - b.r) + Math.abs(a.g - b.g) + Math.abs(a.b - b.b) < 0.03;
export function colourWord(c: InstrumentColor): string {
  return STAGE_COLORS.find((s) => near(c, s.rgb))?.label ?? 'a custom colour';
}
const barsWord = (n: number) => (Math.abs(n - Math.round(n)) < 1e-6 ? `${Math.round(n)} bar${Math.round(n) === 1 ? '' : 's'}` : `${n} bars`);

/** The block's panel. Every change is ONE undo step (`replaceStep`). */
export function DrumBlockPanel(props: {
  timeline: Timeline; lane: TimelineLane; step: HitBlockStep; sig: number; bpm: number;
  /** Why it won't play where it plays, or null. */
  wont: string | null;
  /** The timeline's PC plays drum lights (`timeline_instrument_routing`). */
  playsHere: boolean;
  Prop: PropFn;
  onRemove: () => void; onDuplicate: () => void;
}) {
  const P = props.Prop;
  const b = () => props.step;
  const set = (next: HitBlockStep) => replaceStep(props.timeline.id, props.lane.id, next);
  const cycles = () => hitBlockCycles(b()).length;
  const fill = () => b().fill;
  const fillOn = () => !!hitBlockFill(b());
  const strobeAsked = () => b().look.style === 'strobe' || (fillOn() && b().fill!.look.style === 'strobe');
  const lengthTo = (bars: number) => placeStep(props.timeline.id, props.lane.id, b().id, { durationBars: Math.max(1, bars) });
  return (
    <>
      <P label="Drum">
        <div class="tl2-seg tl2-gridseg" data-testid="tl2-drum-pick">
          <For each={DRUMS}>
            {(d) => (
              <button type="button" aria-pressed={b().source.role === d.role && !b().source.port}
                data-info={`${d.name}: OP-MidiFeed's ${d.name.toLowerCase()} hits light this block.`}
                onClick={() => set({ ...b(), source: { role: d.role } })}>{d.name}</button>
            )}
          </For>
        </div>
      </P>
      <Show when={!b().source.role || b().source.port || b().source.track}>
        <P label=""><div class="tl2-text tl2-dim">Listening for: {sourceLabel(b().source)}</div></P>
      </Show>
      <P label="Repeats">
        <div class="tl2-seg tl2-gridseg" data-testid="tl2-drum-cycle">
          <For each={CYCLE_CHOICES}>
            {(n) => (
              <button type="button" aria-pressed={b().cycleBars === n} disabled={n > Math.max(1, b().durationBars) + 1e-9}
                data-info={`Every ${barsWord(n)}: the look plays, then the fill at the end, then again.`}
                onClick={() => {
                  const f = b().fill;
                  set({ ...b(), cycleBars: n, ...(f ? { fill: { ...f, bars: Math.min(f.bars, Math.max(0.25, n / 2)) } } : {}) });
                }}>{n}</button>
            )}
          </For>
        </div>
        <div class="tl2-text tl2-dim">Every {barsWord(b().cycleBars)}, {cycles()} time{cycles() === 1 ? '' : 's'} over {barsWord(b().durationBars)}</div>
      </P>
      <P label="Length">
        <div class="tl2-stepper" data-testid="tl2-drum-length">
          <button type="button" aria-label="One bar shorter" disabled={b().durationBars <= 1} onClick={() => lengthTo(b().durationBars - 1)}>-</button>
          <span>{barsWord(b().durationBars)}</span>
          <button type="button" aria-label="One bar longer" onClick={() => lengthTo(b().durationBars + 1)}>+</button>
        </div>
      </P>
      <LookEditor P={P} look={b().look} testid="main" onChange={(look) => set({ ...b(), look })} />
      <P label="Fill">
        <div class="tl2-seg tl2-gridseg" data-testid="tl2-drum-fill">
          <button type="button" aria-pressed={!fillOn()} data-info="No fill: the look plays the whole cycle."
            onClick={() => { const f = fill(); if (f) set({ ...b(), fill: { ...f, off: true } }); }}>Off</button>
          <For each={FILL_CHOICES}>
            {(c) => (
              <button type="button" aria-pressed={fillOn() && Math.abs(fill()!.bars - c.bars) < 1e-6} disabled={c.bars >= b().cycleBars - 1e-9}
                data-info={`The fill: the ${c.name.toLowerCase()} of every ${barsWord(b().cycleBars)} lights its own look.`}
                onClick={() => {
                  const f = fill();
                  const look = f?.look ?? { lights: [], allLights: true, colors: [{ ...WHITE }], style: 'flash' as const, vary: 'none' as const, fallBeats: 0.5, velocitySensitivity: 0 };
                  set({ ...b(), fill: { bars: c.bars, look } });
                }}>{c.name}</button>
            )}
          </For>
        </div>
      </P>
      <Show when={fillOn()}>
        <div class="tl2-subhead">In the fill</div>
        <LookEditor P={P} look={b().fill!.look} testid="fill" onChange={(look) => set({ ...b(), fill: { ...b().fill!, look } })} />
      </Show>
      <TryIt block={b()} sig={props.sig} bpm={props.bpm} />
      <Show when={strobeAsked()}>
        <P label="Strobes">
          <div class="tl2-togs">
            <button type="button" class="tl2-btn" aria-pressed={props.timeline.strobes === true} data-testid="tl2-drum-strobes"
              data-info="Strobes are off in a show until you allow them (flash safety). Allowed, a strobe look strobes; OPBridge's flash limit still applies."
              onClick={() => setTimelineStrobes(props.timeline.id, props.timeline.strobes !== true)}>
              {props.timeline.strobes === true ? 'Allowed in this show' : 'Allow strobes in this show'}
            </button>
          </div>
          <Show when={props.timeline.strobes !== true}>
            <div class="tl2-text tl2-dim">Until you allow them, a strobe look lights nothing on the rig.</div>
          </Show>
        </P>
      </Show>
      <Show when={props.wont}>
        <P label="Plays"><div class="tl2-text tl2-warn">Won't play: {props.wont}</div></P>
      </Show>
      <Show when={!props.playsHere && !props.wont}>
        <P label="Plays"><div class="tl2-text tl2-warn">The OPBridge this timeline plays on needs an update for drum lights.</div></P>
      </Show>
      <P label="On the rig">
        <div class="tl2-text tl2-dim">
          Your drums light it live: OP-MidiFeed on the drum track in Live. OPBridge lets through 3 new flashes a second
          unless its Flash limit is raised, so a fast fill flashes fewer times than you play it.
          <Show when={props.timeline.testTrack}> While this timeline plays its song, OPBridge lights it from the song, not from your drums.</Show>
        </div>
      </P>
      <div class="tl2-actions">
        <button type="button" class="tl2-btn" data-info="A copy of this block, straight after it." onClick={() => props.onDuplicate()}>Duplicate</button>
        <button type="button" class="tl2-btn tl2-danger" onClick={() => props.onRemove()}>Delete</button>
      </div>
    </>
  );
}

/** One look: its lights, colours, how it moves, how a hit looks and how softer hits show. */
function LookEditor(props: { P: PropFn; look: HitBlockLook; testid: string; onChange: (l: HitBlockLook) => void }) {
  const P = props.P;
  const l = () => props.look;
  const rig = () => lookableLights(fixtures());
  const lit = (id: string) => l().allLights || l().lights.includes(id);
  const [colourAt, setColourAt] = createSignal(0);
  const at = () => Math.min(colourAt(), Math.max(0, l().colors.length - 1));
  function toggleLight(id: string) {
    const order = rig().map((f) => f.id);
    const now = l().allLights ? order : l().lights;
    const next = now.includes(id) ? now.filter((x) => x !== id) : [...now, id];
    const { allLights: _a, ...rest } = l();
    void _a;
    props.onChange({ ...rest, lights: next.sort((a, b) => order.indexOf(a) - order.indexOf(b)) });
  }
  const colourLine = () => {
    const cs = l().colors;
    if (cs.length <= 1) return `Every hit is ${colourWord(cs[0] ?? WHITE)}.`;
    const words = cs.map(colourWord).join(', then ');
    const n = l().allLights ? rig().length : l().lights.length;
    return l().vary === 'next' && cs.length === n
      ? `Each light keeps its own colour as the chase goes round: ${words}.`
      : `Each hit takes the next colour: ${words}, and round again.`;
  };
  return (
    <>
      <P label="Lights">
        <div class="tl2-chips" data-testid={`tl2-drum-lights-${props.testid}`}>
          <button type="button" class="tl2-chip" aria-pressed={!!l().allLights}
            data-info="Every light this timeline's computer drives (never the ceiling's Hue spots). A light patched later joins."
            onClick={() => {
              if (l().allLights) { const { allLights: _a, ...rest } = l(); void _a; props.onChange({ ...rest, lights: rig().map((f) => f.id) }); }
              else props.onChange({ ...l(), allLights: true });
            }}>Every light</button>
          <For each={rig()}>
            {(f) => (
              <button type="button" class="tl2-chip" aria-pressed={lit(f.id)} data-light={f.id}
                data-info={`${f.name}: tap to ${lit(f.id) ? 'leave it out' : 'add it'}.`}
                onClick={() => toggleLight(f.id)}>{f.name || f.id}</button>
            )}
          </For>
        </div>
        <Show when={rig().length === 0}>
          <div class="tl2-text tl2-dim">No lights in your rig yet: patch them on OPBridge first.</div>
        </Show>
        <Show when={rig().length > 0 && !l().allLights && l().lights.length === 0}>
          <div class="tl2-text tl2-warn">Pick at least one light.</div>
        </Show>
      </P>
      <P label="Colours">
        <div class="tl2-chips" data-testid={`tl2-drum-colours-${props.testid}`}>
          <For each={l().colors}>
            {(c, i) => (
              <span class="tl2-cchip" aria-selected={at() === i()}>
                <button type="button" class="tl2-swatch" style={{ background: cssColour(c) }} aria-label={`Colour ${i() + 1}: ${colourWord(c)}`}
                  title={colourWord(c)} onClick={() => setColourAt(i())} />
                <Show when={l().colors.length > 1}>
                  <button type="button" class="tl2-cx" aria-label={`Remove ${colourWord(c)}`}
                    onClick={() => { props.onChange({ ...l(), colors: l().colors.filter((_, k) => k !== i()) }); setColourAt(Math.max(0, i() - 1)); }}>×</button>
                </Show>
              </span>
            )}
          </For>
          <Show when={l().colors.length < 8}>
            <button type="button" class="tl2-chip" data-info="Add a colour: hits then take the colours in turn."
              onClick={() => { const cs = l().colors; props.onChange({ ...l(), colors: [...cs, { ...(cs[cs.length - 1] ?? WHITE) }] }); setColourAt(cs.length); }}>+ colour</button>
          </Show>
        </div>
        <div class="tl2-swatches tl2-swatches-d">
          <For each={STAGE_COLORS.filter((c) => c.label !== 'Off')}>
            {(c) => (
              <button type="button" class="tl2-swatch" style={{ background: cssColour(c.rgb) }} title={c.label} aria-label={c.label}
                aria-pressed={!!l().colors[at()] && near(l().colors[at()]!, c.rgb)}
                onClick={() => props.onChange({ ...l(), colors: l().colors.length ? l().colors.map((x, k) => (k === at() ? { ...c.rgb } : x)) : [{ ...c.rgb }] })} />
            )}
          </For>
        </div>
        <div class="tl2-text tl2-dim">{colourLine()}</div>
      </P>
      <P label="Moves">
        <div class="tl2-seg tl2-gridseg2" data-testid={`tl2-drum-vary-${props.testid}`}>
          <For each={VARIES}>
            {(v) => <button type="button" aria-pressed={l().vary === v} data-info={VARY_HINTS[v]} onClick={() => props.onChange({ ...l(), vary: v })}>{VARY_NAMES[v]}</button>}
          </For>
        </div>
        <div class="tl2-text tl2-dim">{VARY_HINTS[l().vary]}</div>
      </P>
      <P label="Each hit">
        <div class="tl2-seg" data-testid={`tl2-drum-style-${props.testid}`}>
          <For each={STYLES}>
            {(s) => <button type="button" aria-pressed={l().style === s} data-info={STYLE_HINTS[s]} onClick={() => props.onChange({ ...l(), style: s })}>{STYLE_NAMES[s]}</button>}
          </For>
        </div>
        <div class="tl2-seg tl2-sub">
          <For each={FALL_CHOICES}>
            {(f) => (
              <button type="button" aria-pressed={Math.abs(l().fallBeats - f.beats) < 1e-6}
                data-info={`${f.name}: a hit lasts ${f.beats === 1 ? 'a beat' : `${f.beats} beat${f.beats === 1 ? '' : 's'}`} before it has faded back.`}
                onClick={() => props.onChange({ ...l(), fallBeats: f.beats })}>{f.name}</button>
            )}
          </For>
        </div>
      </P>
      <P label="Softer hits">
        <div class="tl2-seg" data-testid={`tl2-drum-soft-${props.testid}`}>
          <For each={SOFT_CHOICES}>
            {(c) => (
              <button type="button" aria-pressed={Math.abs((l().velocitySensitivity ?? 1) - c.s) < 1e-6} data-info={c.hint}
                onClick={() => {
                  const { velocitySensitivity: _v, ...rest } = l();
                  void _v;
                  props.onChange(c.s === 1 ? rest : { ...rest, velocitySensitivity: c.s });
                }}>{c.name}</button>
            )}
          </For>
        </div>
      </P>
      <P label="Brightness">
        <div class="tl2-range">
          <input type="range" min="10" max="100" step="5" value={Math.round((l().level ?? 1) * 100)} aria-label="Brightness"
            onChange={(e) => { const v = Number(e.currentTarget.value) / 100; const { level: _l, ...rest } = l(); void _l; props.onChange(v >= 1 ? rest : { ...rest, level: v }); }} />
          <span>{Math.round((l().level ?? 1) * 100)}%</span>
        </div>
      </P>
    </>
  );
}

/**
 * TRY IT: the block's lights on screen, lit the way the rig lights them (`previewColours`, OPBridge's rules), at the
 * timeline's tempo. "Hit" plays one hit of the look (and "Fill hit" one of the fill); "Play a beat" plays a groove
 * that runs from a bar before the fill, through it, into the next cycle, and loops, so the switch is seen.
 */
function TryIt(props: { block: HitBlockStep; sig: number; bpm: number }) {
  const rig = () => fixtures();
  const mainLights = () => blockLookLights(props.block.look, rig());
  const fillInfo = () => hitBlockFill(props.block);
  const fillLights = () => { const f = fillInfo(); return f ? blockLookLights(f.look, rig()) : []; };
  const shown = createMemo(() => {
    const want = new Set([...mainLights(), ...fillLights()]);
    return rig().filter((f) => want.has(f.id));
  });
  const bpb = () => Math.max(1, props.sig);
  const bpm = () => (Number.isFinite(props.bpm) && props.bpm > 20 ? props.bpm : 120);
  const t0 = performance.now();
  const beatNow = () => ((performance.now() - t0) / 1000) * (bpm() / 60);
  const [now, setNow] = createSignal(0);
  const [playing, setPlaying] = createSignal(false);
  const [bar, setBar] = createSignal<{ n: number; inFill: boolean } | null>(null);
  let mainHits: PreviewHit[] = [];
  let fillHits: PreviewHit[] = [];
  let mainStep = 0;
  let fillStep = 0;
  let raf = 0;
  let quietFrom = 0;
  // The groove: from a bar before the fill (or the last two bars) to one bar into the next cycle, looping.
  let grooveAt = 0;
  let cursor = 0;
  const loopPlan = () => {
    const cyc = props.block.cycleBars;
    const f = fillInfo();
    const fillFrom = f ? Math.max(0, cyc - f.bars) : null;
    const startBar = Math.max(0, Math.floor((fillFrom ?? cyc) - 1 - 1e-9));
    const cycle = grooveFor(props.block.source.role, cyc, fillFrom, bpb());
    const startBeat = startBar * bpb();
    const events = [
      ...cycle.filter((e) => e.beat >= startBeat - 1e-9).map((e) => ({ ...e, at: e.beat - startBeat, inFill: fillFrom !== null && e.beat >= fillFrom * bpb() - 1e-9 })),
      ...cycle.filter((e) => e.beat < bpb() - 1e-9).map((e) => ({ ...e, at: cyc * bpb() - startBeat + e.beat, inFill: false })),
    ];
    return { events, length: (cyc + 1 - startBar) * bpb(), startBar, cyc, fillFrom };
  };
  function tick() {
    const b = beatNow();
    if (playing()) {
      const plan = loopPlan();
      const n = plan.events.length;
      if (n > 0 && plan.length > 0) {
        for (let guard = 0; guard < 256; guard++) {
          const loop = Math.floor(cursor / n); const e = plan.events[cursor % n]!;
          const t = grooveAt + loop * plan.length + e.at;
          if (t > b) break;
          if (e.inFill) fillHits.push({ atBeat: t, step: fillStep++, velocity: e.velocity });
          else mainHits.push({ atBeat: t, step: mainStep++, velocity: e.velocity });
          cursor++;
        }
        const within = (b - grooveAt) % plan.length;
        const barNo = Math.floor((plan.startBar * bpb() + within) / bpb());
        const inCycle = barNo % plan.cyc;
        setBar({ n: inCycle + 1, inFill: plan.fillFrom !== null && inCycle >= Math.floor(plan.fillFrom + 1e-9) });
      }
      quietFrom = b;
    }
    mainHits = mainHits.filter((h) => b - h.atBeat < 8);
    fillHits = fillHits.filter((h) => b - h.atBeat < 8);
    setNow(b);
    raf = playing() || b - quietFrom < 3 ? requestAnimationFrame(tick) : 0;
  }
  const wake = () => { if (!raf) raf = requestAnimationFrame(tick); };
  onCleanup(() => { if (raf) cancelAnimationFrame(raf); });
  function hit(which: 'main' | 'fill') {
    const b = beatNow();
    quietFrom = b;
    if (which === 'main') mainHits.push({ atBeat: b, step: mainStep++, velocity: 120 });
    else fillHits.push({ atBeat: b, step: fillStep++, velocity: 120 });
    wake();
  }
  function toggleBeat() {
    if (playing()) { setPlaying(false); setBar(null); return; }
    grooveAt = beatNow() + 0.25;
    cursor = 0;
    setPlaying(true);
    wake();
  }
  const colours = createMemo(() => {
    now();
    const f = fillInfo();
    return previewColours([
      { look: props.block.look, lights: mainLights(), hits: mainHits },
      ...(f ? [{ look: f.look, lights: fillLights(), hits: fillHits }] : []),
    ], now());
  });
  return (
    <div class="tl2-tryit" data-testid="tl2-drum-tryit">
      <div class="tl2-tryhead">
        <b>Try it</b>
        <span>{bar() ? `Bar ${bar()!.n} of ${props.block.cycleBars}${bar()!.inFill ? ': the fill' : ''}` : `${Math.round(bpm())} BPM`}</span>
      </div>
      <div class="tl2-rigdots">
        <For each={shown()} fallback={<span class="tl2-text tl2-dim">No lights picked.</span>}>
          {(f) => {
            const c = () => colours().get(f.id) ?? null;
            const lvl = () => { const x = c(); return x ? Math.max(x.r, x.g, x.b) : 0; };
            return (
              <div class="tl2-rigdot" data-light={f.id} data-lit={lvl() > 0.04 ? 'true' : undefined}>
                <i style={{ background: c() ? cssColour(c()) : undefined, 'box-shadow': lvl() > 0.04 ? `0 0 ${Math.round(4 + 14 * lvl())}px ${cssColour(c())}` : undefined }} />
                <span>{f.name || f.id}</span>
              </div>
            );
          }}
        </For>
      </div>
      <div class="tl2-togs">
        <button type="button" class="tl2-btn" data-testid="tl2-drum-hit" data-info="One hit of the look, as a drum would play it." onClick={() => hit('main')}>Hit</button>
        <Show when={fillInfo()}>
          <button type="button" class="tl2-btn" data-testid="tl2-drum-fillhit" data-info="One hit of the fill." onClick={() => hit('fill')}>Fill hit</button>
        </Show>
        <button type="button" class="tl2-btn" aria-pressed={playing()} data-testid="tl2-drum-beat"
          data-info="A beat at this timeline's tempo, from a bar before the fill, through it and on: what this block does with a drummer."
          onClick={toggleBeat}>{playing() ? 'Stop' : 'Play a beat'}</button>
      </div>
      <div class="tl2-text tl2-dim">A preview on this screen; on the rig the hits come from your drums.</div>
    </div>
  );
}

/** A plain route (one a show or another app wrote, rather than a block): what it listens for and which look it lights. */
export function DrumRoutePanel(props: { timeline: Timeline; step: { source: HitBlockStep['source']; lookId: string }; Prop: PropFn; onRemove: () => void }) {
  const P = props.Prop;
  const look = () => (props.timeline.instrumentLooks ?? []).find((l) => l.id === props.step.lookId);
  return (
    <>
      <P label="Listens for"><div class="tl2-text">{sourceLabel(props.step.source)}</div></P>
      <P label="Lights">
        <div class="tl2-text">{look() ? (look()!.name ?? look()!.id) : props.step.lookId.startsWith('~') ? 'Nothing (it ends what came before)' : 'A look this timeline no longer has'}</div>
      </P>
      <P label=""><div class="tl2-text tl2-dim">From here until the next one for the same drum.</div></P>
      <div class="tl2-actions">
        <button type="button" class="tl2-btn tl2-danger" onClick={() => props.onRemove()}>Delete</button>
      </div>
    </>
  );
}
